import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  IonBackButton,
  IonButton,
  IonInput,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonPage,
  IonSegment,
  IonSegmentButton,
  IonSpinner,
  IonTitle,
  IonToast,
  IonToolbar,
} from '@ionic/react';
import type { SupplyRequestLine, DestinationWard } from '@trackeat/shared';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { getSupabase } from '../../lib/supabase.js';

// T-282 — Shell minimale del PREPOSTO (utente esterno scoped a una destinazione).
// Pagina autosufficiente (NON tocca SupplyRequestsPage). Legge order_catalog_rows
// (allow-list RLS), scrive l'ordine in supply_requests.lines[].description, mostra
// il cassetto DDT (shipments emessi verso la sua destinazione, sola lettura).
//
// T-379 — "invio = salvataggio" (blocco soft, spec owner): UN solo ordine attivo
// per (destinazione, giorno). Al cambio data la pagina RICARICA quanto già
// inviato; re-inviare AGGIORNA lo stesso ordine (correzioni) finché è
// 'submitted'. I servizi già ACCETTATI dalla cucina diventano sola lettura.
// Così la stessa destinazione non può più accumulare ordini doppi che gonfiano
// le stampe di produzione.

interface CatalogRow {
  id: string;
  category: string;
  label: string;
  sort_order: number;
}
interface ExistingOrder {
  id: string;
  status: string;
  lines: SupplyRequestLine[] | null;
  submitted_at: string | null;
  created_at: string;
}
type MealService = 'pranzo' | 'cena' | 'colazione';

const CATEGORY_ORDER = ['PRIMI', 'SECONDI', 'CONTORNI', 'FRUTTA'] as const;
const CAT_COLOR: Record<string, string> = {
  PRIMI: '#e0992f',
  SECONDI: '#d1544f',
  CONTORNI: '#3fa56a',
  FRUTTA: '#8a63d2',
};
const MEAL_SERVICES: ReadonlyArray<readonly [MealService, string]> = [
  ['pranzo', 'Pranzo'],
  ['cena', 'Cena'],
  ['colazione', 'Colazione'],
];
const todayIso = (): string => new Date().toISOString().slice(0, 10);
const norm = (s: string): string => s.trim().toLowerCase();
// T-421: chiave quantità PER (servizio, reparto, riga). `ward` è il NOME del
// reparto o '' quando la destinazione non ha reparti (ordine a livello
// destinazione) → in quel caso la chiave è `svc||rowId`, comportamento identico
// a prima. Keying separato = Pranzo/Cena e i vari reparti non si mescolano.
const qKey = (svc: MealService, ward: string, rowId: string): string => `${svc}|${ward}|${rowId}`;
// Chiave del "già accettato" (sola lettura), per (servizio, reparto, descrizione).
const lockKey = (svc: MealService, ward: string, description: string): string => `${svc}|${ward}|${norm(description)}`;
// Pranzo/cena/colazione = ordini separati (una riga supply_requests per servizio).
const MEALS: MealService[] = ['colazione', 'pranzo', 'cena'];
const rowMealOf = (o: ExistingOrder): MealService => {
  for (const ln of o.lines ?? []) return (ln.meal_service ?? 'pranzo') as MealService;
  return 'pranzo';
};

export default function PrepostoOrderPage(): JSX.Element {
  const { profile, signOut } = useAuth();
  const sb = useMemo(() => getSupabase(), []);
  const preposto = profile?.preposto ?? null;

  const [loading, setLoading] = useState(true);
  const [dayLoading, setDayLoading] = useState(false);
  const [destName, setDestName] = useState('');
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [rows, setRows] = useState<CatalogRow[]>([]);
  // T-421: reparti della destinazione (opzionali) + modello (sottoinsieme catalogo).
  const [wards, setWards] = useState<DestinationWard[]>([]);
  const [orderModel, setOrderModel] = useState<string[] | null>(null);
  const [selectedWard, setSelectedWard] = useState<string>(''); // '' = nessun reparto
  const [qty, setQty] = useState<Record<string, string>>({});
  const [serviceDate, setServiceDate] = useState<string>(todayIso());
  const [mealService, setMealService] = useState<MealService>('pranzo');
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  // T-379/T-443: stato dell'ordine già presente sul server, PER SERVIZIO (pranzo/cena
  // sono ordini separati: una riga supply_requests per servizio, inviabile da sola).
  const [editableByMeal, setEditableByMeal] = useState<Record<MealService, { id: string; submitted_at: string | null } | null>>(() => ({ colazione: null, pranzo: null, cena: null }));
  const [legacyDupes, setLegacyDupes] = useState<string[]>([]);
  const [lockedServices, setLockedServices] = useState<ReadonlySet<MealService>>(new Set());
  const [lockedQty, setLockedQty] = useState<Record<string, number>>({});
  // Righe dell'ordine inviato che non trovano più il catalogo (label cambiata) o
  // che sono bed-level (ordine reparto): non mostrabili come stepper ma da NON perdere
  // al re-invio. PER SERVIZIO (si preservano sulla riga del loro servizio).
  const [orphansByMeal, setOrphansByMeal] = useState<Record<MealService, SupplyRequestLine[]>>(() => ({ colazione: [], pranzo: [], cena: [] }));
  // T-406 (A6): righe a TESTO LIBERO (fuori catalogo). T-421 FIX (review high):
  // ogni riga porta il PROPRIO servizio + reparto catturati alla creazione, così
  // cambiando reparto/servizio le righe già scritte non vengono riattribuite.
  const [freeLines, setFreeLines] = useState<{ description: string; qty: string; meal_service: MealService; reparto: string }[]>([]);
  // Ordine editabile del servizio CORRENTE (pranzo/cena = ordini separati).
  const editable = editableByMeal[mealService];
  const orphanLines = orphansByMeal[mealService];

  useEffect(() => {
    if (!preposto) return;
    let mounted = true;
    void (async () => {
      // T-427: rimossa la query DDT (il preposto ordina; i documenti di
      // trasporto non gli servono in questa schermata).
      const [d, c] = await Promise.all([
        sb.from('destinations').select('name,business_id,order_model,wards').eq('id', preposto.destination_id).maybeSingle(),
        sb.from('order_catalog_rows').select('id,category,label,sort_order').eq('active', true).order('sort_order'),
      ]);
      if (!mounted) return;
      const dd = (d.data as { name?: string; business_id?: string | null; order_model?: string[] | null; wards?: DestinationWard[] | null } | null) ?? null;
      setDestName(dd?.name ?? '');
      setBusinessId(dd?.business_id ?? null);
      setRows((c.data as CatalogRow[] | null) ?? []);
      // T-421: modello (null = catalogo completo) + reparti ordinati.
      const model = Array.isArray(dd?.order_model) && dd!.order_model!.length > 0 ? dd!.order_model! : null;
      setOrderModel(model);
      const wl = (dd?.wards ?? []).slice().sort((a, b) => a.sort_order - b.sort_order);
      setWards(wl);
      setSelectedWard(wl.length > 0 ? wl[0].name : '');
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, [sb, preposto]);

  // T-379: carica l'ordine ATTIVO del giorno (submitted = correggibile) e i
  // servizi già accettati (sola lettura). È ciò che rende l'invio un salvataggio.
  const loadDay = useCallback(async (): Promise<void> => {
    if (!preposto) return;
    setDayLoading(true);
    const { data, error } = await sb
      .from('supply_requests')
      .select('id,status,lines,submitted_at,created_at')
      .eq('destination_id', preposto.destination_id)
      .eq('requested_for_date', serviceDate)
      .is('archived_at', null)
      .in('status', ['submitted', 'accepted', 'in_production', 'fulfilled'])
      .order('created_at', { ascending: false });
    if (error) {
      setDayLoading(false);
      setToast(`Errore caricamento ordine: ${error.message}`);
      return;
    }
    const orders = (data ?? []) as ExistingOrder[];
    const accepted = orders.filter((o) => o.status !== 'submitted');
    const submitted = orders.filter((o) => o.status === 'submitted');

    const locked = new Set<MealService>();
    const lq: Record<string, number> = {};
    for (const o of accepted) {
      for (const ln of o.lines ?? []) {
        const svc = (ln.meal_service ?? 'pranzo') as MealService;
        locked.add(svc);
        // T-421: chiave locked per (servizio, reparto, descrizione).
        const key = lockKey(svc, ln.reparto ?? '', ln.description ?? '');
        lq[key] = (lq[key] ?? 0) + (Number(ln.qty) || 0);
      }
    }

    // Un ordine correggibile PER SERVIZIO: pranzo e cena sono righe separate,
    // ognuna inviabile/correggibile da sola. Le righe submitted si raggruppano per
    // servizio; per ogni servizio l'ordine è la riga più recente (eventuali doppioni
    // legacy DELLO STESSO servizio si bonificano al prossimo invio).
    const submittedByMeal: Record<MealService, ExistingOrder[]> = { colazione: [], pranzo: [], cena: [] };
    for (const o of submitted) submittedByMeal[rowMealOf(o)].push(o);

    const rowIdByLabel = new Map(rows.map((r) => [norm(r.label), r.id]));
    const q: Record<string, string> = {};
    const orphansByMealNext: Record<MealService, SupplyRequestLine[]> = { colazione: [], pranzo: [], cena: [] };
    const editableByMealNext: Record<MealService, { id: string; submitted_at: string | null } | null> = { colazione: null, pranzo: null, cena: null };
    const dupes: string[] = [];
    for (const m of MEALS) {
      const rowsForMeal = submittedByMeal[m];
      const cur = rowsForMeal[0] ?? null;
      editableByMealNext[m] = cur ? { id: cur.id, submitted_at: cur.submitted_at } : null;
      const orphans: SupplyRequestLine[] = [];
      for (const ln of cur?.lines ?? []) {
        const svc = (ln.meal_service ?? 'pranzo') as MealService;
        if (locked.has(svc)) continue; // il servizio accettato prevale
        // Le righe per-LETTO (ordine reparto) si preservano verbatim (orphan): non
        // vanno mai piegate in una cella catalogo (perderebbero letto/note).
        if ((ln.letto ?? '').trim()) { orphans.push(ln); continue; }
        const n = Number(ln.qty) || 0;
        if (n <= 0) continue;
        const rid = rowIdByLabel.get(norm(ln.description ?? ''));
        if (rid) q[qKey(svc, ln.reparto ?? '', rid)] = String(n);
        else if ((ln.description ?? '').trim()) orphans.push(ln);
      }
      orphansByMealNext[m] = orphans;
      dupes.push(...rowsForMeal.slice(1).map((o) => o.id)); // doppioni dello STESSO servizio
    }
    setQty(q);
    setOrphansByMeal(orphansByMealNext);
    setLockedServices(locked);
    setLockedQty(lq);
    setEditableByMeal(editableByMealNext);
    setLegacyDupes(dupes);
    setDayLoading(false);
  }, [sb, preposto, serviceDate, rows]);

  useEffect(() => {
    if (loading) return;
    void loadDay();
  }, [loading, loadDay]);

  // T-421: righe visibili = modello della destinazione (o catalogo intero).
  const visibleRows = useMemo(
    () => (orderModel ? rows.filter((r) => orderModel.includes(r.label)) : rows),
    [rows, orderModel],
  );
  const grouped = useMemo(() => {
    const m: Record<string, CatalogRow[]> = {};
    for (const r of visibleRows) (m[r.category] ??= []).push(r);
    return m;
  }, [visibleRows]);

  const orphanTotal = useMemo(
    () => orphanLines.reduce((acc, l) => acc + (Number(l.qty) || 0), 0),
    [orphanLines],
  );
  const freeTotal = useMemo(
    () => freeLines.reduce((acc, l) => acc + (parseInt(l.qty, 10) || 0), 0),
    [freeLines],
  );
  const totalMeals = useMemo(
    () => Object.values(qty).reduce((acc, v) => acc + (parseInt(v, 10) || 0), 0) + orphanTotal + freeTotal,
    [qty, orphanTotal, freeTotal],
  );
  // T-421: un servizio ha quantità se ANY reparto ha qty>0 su quel servizio.
  const serviceHasQty = useCallback(
    (svc: MealService): boolean =>
      Object.entries(qty).some(([k, v]) => k.startsWith(`${svc}|`) && (parseInt(v, 10) || 0) > 0),
    [qty],
  );
  const currentLocked = lockedServices.has(mealService);

  const setQ = useCallback(
    (id: string, n: number) => {
      setQty((q) => ({ ...q, [qKey(mealService, selectedWard, id)]: n > 0 ? String(n) : '' }));
    },
    [mealService, selectedWard],
  );
  const cur = useCallback(
    (id: string) => parseInt(qty[qKey(mealService, selectedWard, id)] ?? '', 10) || 0,
    [qty, mealService, selectedWard],
  );

  const submit = useCallback(async () => {
    if (!preposto || dayLoading) return;
    if (!businessId) {
      setToast('La destinazione non ha un centro cottura associato.');
      return;
    }
    // Righe del SOLO servizio corrente (pranzo/cena = ordini separati → una riga per
    // servizio) × TUTTI i reparti, più le righe fuori catalogo/bed-level da preservare.
    const svc = mealService;
    if (lockedServices.has(svc)) { setToast('Questo servizio è già stato accettato dalla cucina.'); return; }
    const wardCtx: string[] = wards.length > 0 ? wards.map((w) => w.name) : [''];
    const lines: SupplyRequestLine[] = [];
    for (const w of wardCtx) {
      // Iterare sul catalogo COMPLETO `rows` (non `visibleRows`): una quantità già
      // ordinata su una riga poi esclusa dal modello resta in `qty` e va comunque
      // ri-serializzata, altrimenti sparirebbe muta.
      for (const r of rows) {
        const n = parseInt(qty[qKey(svc, w, r.id)] ?? '', 10);
        if (Number.isFinite(n) && n > 0) {
          lines.push({ meal_service: svc, menu_label: null, recipe_id: null, description: r.label, qty: n, uom: 'pasti', reparto: w || null, notes: null });
        }
      }
    }
    // Orphan del servizio corrente (righe bed-level / fuori catalogo) da preservare.
    lines.push(...orphansByMeal[svc]);
    // Righe a testo libero del servizio corrente.
    for (const fl of freeLines) {
      if (fl.meal_service !== svc) continue;
      const n = parseInt(fl.qty, 10);
      if (fl.description.trim() && Number.isFinite(n) && n > 0) {
        lines.push({ meal_service: svc, menu_label: null, recipe_id: null, description: fl.description.trim(), qty: n, uom: 'pasti', reparto: fl.reparto || null, notes: null });
      }
    }
    if (lines.length === 0 && !editable) {
      setToast('Inserisci almeno una quantità.');
      return;
    }
    setSubmitting(true);
    try {
      const nowIso = new Date().toISOString();
      if (editable && lines.length === 0) {
        // Tutto azzerato → ritiro dell'ordine (si può sempre reinviare).
        const { data: upd, error } = await sb
          .from('supply_requests')
          .update({ archived_at: nowIso })
          .eq('id', editable.id)
          .eq('status', 'submitted')
          .select('id');
        if (error) throw error;
        if (!upd || upd.length === 0) {
          setToast('La cucina ha appena accettato questo ordine: non è più modificabile.');
          await loadDay();
          return;
        }
        setToast('Ordine ritirato ✓');
      } else if (editable) {
        // Correzione: si AGGIORNA lo stesso ordine, finché resta 'submitted'.
        // Il filtro sullo status è il guard di concorrenza: se il responsabile
        // lo ha accettato nel frattempo, 0 righe aggiornate → si ricarica.
        const { data: upd, error } = await sb
          .from('supply_requests')
          .update({ lines, submitted_at: nowIso, submitted_by: profile?.id ?? null })
          .eq('id', editable.id)
          .eq('status', 'submitted')
          .select('id');
        if (error) throw error;
        if (!upd || upd.length === 0) {
          setToast('La cucina ha appena accettato questo ordine: non è più modificabile.');
          await loadDay();
          return;
        }
        setToast('Ordine aggiornato ✓');
      } else {
        const { error } = await sb.from('supply_requests').insert({
          tenant_id: preposto.tenant_id,
          business_id: businessId,
          destination_id: preposto.destination_id,
          requested_for_date: serviceDate,
          status: 'submitted',
          lines,
          attachment_paths: [],
          notes: null,
          submitted_at: nowIso,
          submitted_by: profile?.id ?? null,
        });
        if (error) throw error;
        setToast('Ordine inviato ✓');
      }
      // Bonifica: eventuali doppioni 'submitted' legacy dello stesso giorno
      // vengono archiviati (le stampe li sommerebbero → totali gonfiati).
      if (legacyDupes.length > 0) {
        await sb.from('supply_requests').update({ archived_at: nowIso }).in('id', legacyDupes).eq('status', 'submitted');
      }
      setFreeLines((prev) => prev.filter((fl) => fl.meal_service !== mealService)); // solo il servizio inviato
      // Preserva le quantità DIGITATE ma non ancora inviate degli ALTRI servizi: il
      // reload ricostruisce qty dal server, quindi ri-aggiungo le celle locali in corso.
      const otherQty = Object.entries(qty).filter(([k]) => !k.startsWith(`${mealService}|`));
      await loadDay();
      if (otherQty.length > 0) {
        setQty((prev) => {
          const merged = { ...prev };
          for (const [k, v] of otherQty) if (!(k in prev) && v) merged[k] = v;
          return merged;
        });
      }
    } catch (e) {
      setToast(`Errore invio: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSubmitting(false);
    }
  }, [sb, preposto, rows, wards, qty, orphansByMeal, freeLines, lockedServices, mealService, editable, legacyDupes, serviceDate, businessId, profile, dayLoading, loadDay]);

  if (!preposto) {
    return (
      <IonPage>
        <IonContent className="ion-padding">Accesso non valido.</IonContent>
      </IonPage>
    );
  }

  const submittedTime = editable?.submitted_at
    ? new Date(editable.submitted_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
    : null;
  const lockedLabels = MEAL_SERVICES.filter(([v]) => lockedServices.has(v)).map(([, lab]) => lab);
  const buttonLabel = submitting
    ? 'Invio…'
    : editable
      ? totalMeals === 0
        ? 'Ritira ordine'
        : 'Aggiorna ordine'
      : 'Invia ordine';

  return (
    <IonPage>
      <style>{`
        .pz-hero{background:linear-gradient(135deg,#1f4e5c,#2e7d8a);color:#fff;border-radius:16px;padding:16px 18px;margin:4px 0 14px;box-shadow:0 4px 14px rgba(0,0,0,.12)}
        .pz-hero h2{margin:0 0 2px;font-size:20px;font-weight:800}
        .pz-hero .sub{opacity:.85;font-size:13px}
        .pz-controls{display:flex;gap:10px;align-items:center;margin:0 2px 8px}
        .pz-date{flex:0 0 auto}
        .pz-date input{border:1px solid var(--ion-color-step-300,#cfd8dc);border-radius:10px;padding:8px 10px;font-size:14px;background:var(--ion-background-color,#fff);color:inherit}
        .pz-status{border-radius:12px;padding:10px 14px;margin:0 0 12px;font-size:13px;line-height:1.35}
        .pz-status.sent{background:#fff7e6;border:1px solid #f0d9a8;color:#7a5b13}
        .pz-status.ok{background:#e8f5ec;border:1px solid #bfe3cb;color:#1e6b3a}
        .pz-cat{background:var(--ion-card-background,#fff);border-radius:14px;margin:0 0 12px;overflow:hidden;box-shadow:0 1px 6px rgba(0,0,0,.06)}
        .pz-cat-h{font-weight:800;font-size:13px;letter-spacing:.04em;color:#fff;padding:8px 14px}
        .pz-row{display:flex;align-items:center;gap:10px;padding:8px 12px 8px 14px;border-top:1px solid var(--ion-color-step-100,#eef2f3)}
        .pz-row:first-of-type{border-top:none}
        .pz-row .nm{flex:1;font-size:14px;line-height:1.2}
        .pz-stepper{display:flex;align-items:center;gap:2px}
        .pz-stepper .b{width:34px;height:34px;border-radius:9px;border:1px solid var(--ion-color-step-250,#d3dde0);background:var(--ion-background-color,#fff);font-size:20px;font-weight:700;color:#2e7d8a;display:flex;align-items:center;justify-content:center;padding:0}
        .pz-stepper .b:active{background:#e6f0f2}
        .pz-qty{width:48px;text-align:center;font-size:16px;font-weight:700;border:none;background:transparent;color:inherit}
        .pz-qty.on{color:#1f4e5c}
        .pz-locked-qty{min-width:48px;text-align:center;font-size:16px;font-weight:700;color:#1e6b3a}
      `}</style>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/preposto" />
          </IonButtons>
          <IonTitle>Il mio ordine</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => void signOut()}>Esci</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <IonSpinner />
          </div>
        ) : (
          <>
            <div className="pz-hero">
              <h2>{destName || 'La mia struttura'}</h2>
              <div className="sub">Ordine pasti · compila le quantità e invia</div>
            </div>

            <div className="pz-controls">
              <div className="pz-date">
                <input
                  type="date"
                  value={serviceDate}
                  onChange={(e) => setServiceDate(e.target.value || todayIso())}
                  aria-label="Data del servizio"
                />
              </div>
              <IonSegment
                value={mealService}
                onIonChange={(e) => setMealService((e.detail.value as MealService) ?? 'pranzo')}
              >
                {MEAL_SERVICES.map(([v, lab]) => (
                  <IonSegmentButton key={v} value={v}>
                    {lab}
                    {lockedServices.has(v) ? ' ✓' : serviceHasQty(v) ? ' •' : ''}
                  </IonSegmentButton>
                ))}
              </IonSegment>
            </div>

            {/* T-421: selettore REPARTO (solo se la destinazione ne ha). Le
                quantità si inseriscono un reparto alla volta; il totale in basso
                somma tutti i reparti + servizi. */}
            {wards.length > 0 && (
              <div style={{ margin: '0 2px 12px' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', margin: '0 2px 6px' }}>Reparto</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {wards.map((w) => {
                    const active = selectedWard === w.name;
                    // pallino se questo reparto ha già qualche quantità (in qualunque
                    // servizio). Prefix match robusto (niente split, regge nomi con '|').
                    const hasQty = Object.entries(qty).some(([k, v]) =>
                      (parseInt(v, 10) || 0) > 0 && MEAL_SERVICES.some(([svc]) => k.startsWith(`${svc}|${w.name}|`)));
                    return (
                      <button
                        key={w.id}
                        type="button"
                        onClick={() => setSelectedWard(w.name)}
                        style={{
                          fontSize: 13, padding: '6px 12px', borderRadius: 999, cursor: 'pointer',
                          border: `1px solid ${active ? '#1f4e5c' : '#cbd5e1'}`,
                          background: active ? '#1f4e5c' : '#fff',
                          color: active ? '#fff' : '#475569', fontWeight: active ? 700 : 500,
                        }}
                      >
                        {w.name}{hasQty ? ' •' : ''}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {dayLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
                <IonSpinner />
              </div>
            ) : (
              <>
                {editable && (
                  <div className="pz-status sent">
                    📤 Ordine inviato{submittedTime ? ` il ${submittedTime}` : ''} — puoi correggerlo e reinviarlo
                    finché la cucina non lo accetta.
                  </div>
                )}
                {lockedLabels.length > 0 && (
                  <div className="pz-status ok">
                    ✅ {lockedLabels.join(' + ')}: ordine accettato dalla cucina (sola lettura). Per modifiche
                    contatta il centro cottura.
                  </div>
                )}
                {orphanLines.length > 0 && (
                  <div className="pz-status sent">
                    ℹ️ {orphanLines.length} riga/e dell'ordine inviato non sono più a catalogo: verranno mantenute
                    così come sono al prossimo invio.
                  </div>
                )}

                {CATEGORY_ORDER.filter((cat) => (grouped[cat] ?? []).length > 0).map((cat) => (
                  <div className="pz-cat" key={cat}>
                    <div className="pz-cat-h" style={{ background: CAT_COLOR[cat] ?? '#2e7d8a' }}>
                      {cat}
                    </div>
                    {(grouped[cat] ?? []).map((r) => {
                      if (currentLocked) {
                        const n = lockedQty[lockKey(mealService, selectedWard, r.label)] ?? 0;
                        return (
                          <div className="pz-row" key={r.id}>
                            <span className="nm">{r.label}</span>
                            <span className="pz-locked-qty">{n > 0 ? n : '—'}</span>
                          </div>
                        );
                      }
                      const n = cur(r.id);
                      return (
                        <div className="pz-row" key={r.id}>
                          <span className="nm">{r.label}</span>
                          <div className="pz-stepper">
                            <button className="b" type="button" onClick={() => setQ(r.id, n - 1)} aria-label="meno">
                              −
                            </button>
                            <input
                              className={`pz-qty${n > 0 ? ' on' : ''}`}
                              inputMode="numeric"
                              value={qty[qKey(mealService, selectedWard, r.id)] ?? ''}
                              placeholder="0"
                              onChange={(e) => {
                                const v = parseInt(e.target.value, 10);
                                setQ(r.id, Number.isFinite(v) ? v : 0);
                              }}
                            />
                            <button className="b" type="button" onClick={() => setQ(r.id, n + 1)} aria-label="più">
                              +
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </>
            )}

            {/* T-406 (A6) + T-421: righe a testo libero del CONTESTO corrente
                (servizio selezionato + reparto selezionato). Ogni riga è legata al
                proprio contesto, quindi cambiando reparto/servizio si vedono solo
                le sue. Nascoste se il servizio è già accettato (sola lettura). */}
            {!currentLocked && (
            <div style={{ margin: '14px 0 4px' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#475569', margin: '0 4px 6px' }}>
                Righe libere (fuori catalogo){wards.length > 0 && selectedWard ? ` · ${selectedWard}` : ''}
              </div>
              {freeLines.map((fl, i) => ({ fl, i }))
                .filter(({ fl }) => fl.meal_service === mealService && fl.reparto === selectedWard)
                .map(({ fl, i }) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                  <IonInput
                    style={{ flex: 1, '--background': '#fff', border: '1px solid #e2e8f0', borderRadius: 8, '--padding-start': '8px' } as React.CSSProperties}
                    placeholder="Descrizione piatto" value={fl.description}
                    onIonInput={(e) => { const v = String(e.detail.value ?? ''); setFreeLines((p) => p.map((x, j) => (j === i ? { ...x, description: v } : x))); }}
                  />
                  <IonInput
                    type="number" inputMode="numeric" placeholder="qtà"
                    style={{ maxWidth: 64, textAlign: 'right', '--background': '#fff', border: '1px solid #e2e8f0', borderRadius: 8 } as React.CSSProperties}
                    value={fl.qty}
                    onIonInput={(e) => { const v = String(e.detail.value ?? ''); setFreeLines((p) => p.map((x, j) => (j === i ? { ...x, qty: v } : x))); }}
                  />
                  <IonButton fill="clear" color="medium" size="small" onClick={() => setFreeLines((p) => p.filter((_, j) => j !== i))}>✕</IonButton>
                </div>
              ))}
              <IonButton fill="outline" size="small" onClick={() => setFreeLines((p) => [...p, { description: '', qty: '', meal_service: mealService, reparto: selectedWard }])}>+ Aggiungi riga libera</IonButton>
            </div>
            )}
            <div style={{ height: 12 }} />
          </>
        )}
        <IonToast
          isOpen={toast !== null}
          message={toast ?? ''}
          duration={2500}
          onDidDismiss={() => setToast(null)}
        />
      </IonContent>
      {!loading && (
        <IonFooter>
          <IonToolbar>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '4px 12px' }}>
              <div style={{ flex: 1, fontWeight: 700 }}>Totale: {totalMeals} pasti</div>
              <IonButton
                disabled={submitting || dayLoading || (totalMeals === 0 && !editable)}
                onClick={() => void submit()}
              >
                {buttonLabel}
              </IonButton>
            </div>
          </IonToolbar>
        </IonFooter>
      )}
    </IonPage>
  );
}
